import { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { Phone, Loader2, Check, TestTube, X, CheckCircle2, XCircle, Plus, ShieldCheck, UserCog, ScrollText, Eye, AlertCircle, Pencil } from 'lucide-react';
import { ModernScriptCard } from './ModernScriptCard';
import { ObjectionHandlingSection } from './ObjectionHandlingSection';
import { FloatingSaveBar } from './FloatingSaveBar';
import TestAgentModal from '../CallAgent/TestAgentModal';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';
import { getAgentSettings, saveAgentIdentityConfig, generateAgentScript, getUserProfile, getAgentSettingsRow, upsertAgentSettingsRow, getVapiAgents } from '../../services/settingsService';
import type { VapiAgent } from '../../services/settingsService';
import { AgentAvatarUpload } from './AgentAvatarUpload';
import type { CallAgentSettings as CallAgentSettingsType, ObjectionItem, CallScriptDraft, CallScript1, CallScript2 } from '../../types/settings';
import { useAuth } from '../../contexts/AuthContext';
import { canEditAgentSettings } from '../../utils/permissions';
import { supabase } from '../../lib/supabase';
import { getCallScriptDrafts, getCallScriptSectionStatuses, approveSingleCallScriptSection, saveCallScriptAsDraft, CALL_SCRIPT_SECTIONS } from '../../services/templateService';
import type { TemplateStatus, CallScriptSection } from '../../services/templateService';
import { DraftBanner } from './DraftBanner';
import { TEMPLATE_VARIABLES } from '../../hooks/useVariableInsertion';
import { COMMON_NATIONALITIES, COUNTRY_CAPITAL_MAP, COMMON_LOCATIONS, TONE_OPTIONS } from '../../shared/callAgentConstants';
import MessagingStyleTooltip from '../shared/MessagingStyleTooltip';

interface CallAgentSettingsProps {
  onDirtyStateChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  discardRef?: MutableRefObject<(() => void) | null>;
}

export function CallAgentSettings({ onDirtyStateChange, saveRef, discardRef }: CallAgentSettingsProps) {
  const { user } = useAuth();
  const canEdit = canEditAgentSettings(user);
  const [settings, setSettings] = useState<CallAgentSettingsType | null>(null);
  const [originalSettings, setOriginalSettings] = useState<CallAgentSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | undefined>();
  const [activeScript, setActiveScript] = useState<1 | 2 | 3>(1);
  const [showTestModal, setShowTestModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const dirtyCardsRef = useRef(new Set<string>());
  const [phoneNumbers, setPhoneNumbers] = useState<Array<{ value: string; label: string; number: string }>>([]);
  const [draft, setDraft] = useState<CallScriptDraft | null>(null);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [templateStatuses, setTemplateStatuses] = useState<Record<string, TemplateStatus>>({});
  const [approvingSection, setApprovingSection] = useState<string | null>(null);
  const [nationalityInput, setNationalityInput] = useState('');
  const [showNationalitySuggestions, setShowNationalitySuggestions] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);
  const [vapiAgents, setVapiAgents] = useState<VapiAgent[]>([]);
  const [vapiAgentsLoading, setVapiAgentsLoading] = useState(false);
  const [isEditingAgentName, setIsEditingAgentName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const genderHydratedRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      loadDrafts();
    };
    init();
    loadPhoneNumbers();
    loadAgentAvatar();
    loadVapiAgents();
  }, []);

  const loadAgentAvatar = async () => {
    const row = await getAgentSettingsRow('call');
    if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
  };

  const loadVapiAgents = async () => {
    setVapiAgentsLoading(true);
    try {
      const agents = await getVapiAgents();
      setVapiAgents(agents);
    } catch (error) {
      console.error('Error loading VAPI agents:', error);
    }
    setVapiAgentsLoading(false);
  };

  useEffect(() => {
    if (genderHydratedRef.current) return;
    if (!settings?.vapi_agent_id || vapiAgents.length === 0) return;
    const agent = vapiAgents.find(a => a.vapi_agent_internal_id === settings.vapi_agent_id);
    if (!agent) return;
    genderHydratedRef.current = true;
    if (!settings.gender && agent.vapi_agent_gender) {
      const updated = { ...settings, gender: agent.vapi_agent_gender };
      setSettings(updated);
      setOriginalSettings(prev => prev ? { ...prev, gender: agent.vapi_agent_gender! } : prev);
    }
    if (settings.agent_name && settings.agent_name !== agent.agent_name) {
      setVapiAgents(prev => prev.map(a =>
        a.vapi_agent_internal_id === settings.vapi_agent_id
          ? { ...a, agent_name: settings.agent_name }
          : a
      ));
    }
  }, [settings?.vapi_agent_id, vapiAgents]);

  const loadSettings = async () => {
    setLoading(true);
    const data = await getAgentSettings('call');
    if (data && !data.location && !locationInitialized) {
      const profile = await getUserProfile();
      if (profile?.country && COUNTRY_CAPITAL_MAP[profile.country]) {
        data.location = COUNTRY_CAPITAL_MAP[profile.country];
      }
      setLocationInitialized(true);
    }
    if (data?.location) {
      setLocationInput(data.location);
    }
    setSettings(data);
    setOriginalSettings(JSON.parse(JSON.stringify(data)));
    setLoading(false);
  };

  const loadPhoneNumbers = async () => {
    if (!user?.client_id) return;

    try {
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('id, phone_number, country')
        .eq('client_id', user.client_id)
        .eq('is_active', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const options = data.map(item => ({
          value: item.id,
          label: item.country ? `${item.phone_number} (${item.country})` : item.phone_number || '',
          number: item.phone_number || '',
        })).filter(opt => opt.value);
        setPhoneNumbers(options);
      }
    } catch (error) {
      console.error('Error loading phone numbers:', error);
    }
  };

  const loadDrafts = async () => {
    setDraftsLoading(true);
    const callDraft = await getCallScriptDrafts();
    const statuses = await getCallScriptSectionStatuses();
    setTemplateStatuses(statuses);

    if (callDraft?.script_data) {
      const isFreshFromGeneration = callDraft.source === 'sales_materials' && callDraft.status === 'draft';

      if (isFreshFromGeneration) {
        setDraft(callDraft);
      } else {
        const sd = callDraft.script_data;
        setSettings(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (sd.script_1) {
            const s1 = sd.script_1;
            updated.script_1 = {
              greetings: s1.greetings || prev.script_1.greetings,
              introduction: s1.introduction || prev.script_1.introduction,
              why_we_are_calling: s1.why_we_are_calling || prev.script_1.why_we_are_calling,
              elaboration_credibility: s1.elaboration_credibility || prev.script_1.elaboration_credibility,
              engagement: s1.engagement || prev.script_1.engagement,
              qualification: s1.qualification || prev.script_1.qualification,
              next_steps: s1.next_steps || prev.script_1.next_steps,
              booking: s1.booking || prev.script_1.booking,
              closing: s1.closing || prev.script_1.closing,
            };
          }
          if (sd.script_2) {
            const s2 = sd.script_2;
            updated.script_2 = {
              greetings: s2.greetings || prev.script_2.greetings,
              introduction: s2.introduction || prev.script_2.introduction,
              why_we_are_calling_again: s2.why_we_are_calling_again || prev.script_2.why_we_are_calling_again,
              engagement: s2.engagement || prev.script_2.engagement,
              qualification_if_not_done: s2.qualification_if_not_done || prev.script_2.qualification_if_not_done,
              next_steps: s2.next_steps || prev.script_2.next_steps,
              booking: s2.booking || prev.script_2.booking,
              closing: s2.closing || prev.script_2.closing,
            };
          }
          if (sd.objection_handling && sd.objection_handling.length > 0) {
            updated.objection_handling = sd.objection_handling;
          }
          setOriginalSettings(JSON.parse(JSON.stringify(updated)));
          return updated;
        });
        setDraft(null);
      }
    } else {
      setDraft(null);
    }
    setDraftsLoading(false);
  };

  const applyDrafts = () => {
    if (!settings || !draft?.script_data) return;

    const sd = draft.script_data;
    const updated = { ...settings };
    const newStatuses: Record<string, TemplateStatus> = { ...templateStatuses };

    if (sd.script_1) {
      const s1 = sd.script_1;
      updated.script_1 = {
        greetings: s1.greetings || settings.script_1.greetings,
        introduction: s1.introduction || settings.script_1.introduction,
        why_we_are_calling: s1.why_we_are_calling || settings.script_1.why_we_are_calling,
        elaboration_credibility: s1.elaboration_credibility || settings.script_1.elaboration_credibility,
        engagement: s1.engagement || settings.script_1.engagement,
        qualification: s1.qualification || settings.script_1.qualification,
        next_steps: s1.next_steps || settings.script_1.next_steps,
        booking: s1.booking || settings.script_1.booking,
        closing: s1.closing || settings.script_1.closing,
      };
      const s1Keys: (keyof CallScript1)[] = ['greetings', 'introduction', 'why_we_are_calling', 'elaboration_credibility', 'engagement', 'qualification', 'next_steps', 'booking', 'closing'];
      s1Keys.forEach(k => { newStatuses[`script_1_${k}`] = 'draft'; });
    }

    if (sd.script_2) {
      const s2 = sd.script_2;
      updated.script_2 = {
        greetings: s2.greetings || settings.script_2.greetings,
        introduction: s2.introduction || settings.script_2.introduction,
        why_we_are_calling_again: s2.why_we_are_calling_again || settings.script_2.why_we_are_calling_again,
        engagement: s2.engagement || settings.script_2.engagement,
        qualification_if_not_done: s2.qualification_if_not_done || settings.script_2.qualification_if_not_done,
        next_steps: s2.next_steps || settings.script_2.next_steps,
        booking: s2.booking || settings.script_2.booking,
        closing: s2.closing || settings.script_2.closing,
      };
      const s2Keys: (keyof CallScript2)[] = ['greetings', 'introduction', 'why_we_are_calling_again', 'engagement', 'qualification_if_not_done', 'next_steps', 'booking', 'closing'];
      s2Keys.forEach(k => { newStatuses[`script_2_${k}`] = 'draft'; });
    }

    if (sd.objection_handling && sd.objection_handling.length > 0) {
      updated.objection_handling = sd.objection_handling;
      newStatuses['objection_handling'] = 'draft';
    }

    setTemplateStatuses(newStatuses);
    setSettings(updated);
    setDraft(null);
  };

  const handleCardDirtyChange = useCallback((cardKey: string, isDirty: boolean) => {
    if (isDirty) {
      dirtyCardsRef.current.add(cardKey);
    } else {
      dirtyCardsRef.current.delete(cardKey);
    }
    setHasLocalEdits(dirtyCardsRef.current.size > 0);
  }, []);

  useEffect(() => {
    if (settings && originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings) || hasLocalEdits;
      setHasChanges(changed);
      onDirtyStateChange?.(changed);
    }
  }, [settings, originalSettings, hasLocalEdits]);

  useEffect(() => {
    return () => { onDirtyStateChange?.(false); };
  }, []);

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    const success = await saveAgentIdentityConfig('call', settings);

    if (success) {
      const hasDraftSections = Object.values(templateStatuses).some(s => s === 'draft');
      if (hasDraftSections) {
        await saveCallScriptAsDraft(settings, templateStatuses);
      }

      setSaved(true);
      setLastSaved(new Date());
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasChanges(false);
      setTimeout(() => setSaved(false), 2000);
    }

    setSaving(false);
  };

  const handleApproveSection = async (section: CallScriptSection, sectionData: any) => {
    if (!settings) return;

    setApprovingSection(section);
    const success = await approveSingleCallScriptSection(section, sectionData, settings);
    if (success) {
      setTemplateStatuses(prev => ({ ...prev, [section]: 'approved' }));
    }
    setApprovingSection(null);
  };

  const handleApproveObjections = async () => {
    if (!settings) return;

    setApprovingSection('objection_handling');
    const success = await approveSingleCallScriptSection(
      'objection_handling',
      settings.objection_handling,
      settings,
    );
    if (success) {
      setTemplateStatuses(prev => ({ ...prev, objection_handling: 'approved' }));
    }
    setApprovingSection(null);
  };

  const handleDiscard = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasChanges(false);
      onDirtyStateChange?.(false);
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    if (discardRef) discardRef.current = handleDiscard;
  });


  const updateScriptContent = (scriptNum: 1 | 2, section: string, content: string) => {
    if (!settings) return;

    const scriptKey = `script_${scriptNum}` as 'script_1' | 'script_2';
    setSettings({
      ...settings,
      [scriptKey]: {
        ...settings[scriptKey],
        [section]: content,
      },
    });
  };

  const handleGenerateScript = async (scriptNum: 1 | 2, section: string, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'call',
        section: section,
        script_number: scriptNum,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });

      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating script:', error);
      throw error;
    }
  };

  const handleUpdateObjection = (id: string, updates: Partial<ObjectionItem>) => {
    if (!settings) return;

    const updatedObjections = settings.objection_handling.map(obj =>
      obj.id === id ? { ...obj, ...updates } : obj
    );

    setSettings({
      ...settings,
      objection_handling: updatedObjections,
    });
  };

  const handleDeleteObjection = (id: string) => {
    if (!settings) return;

    const updatedObjections = settings.objection_handling.filter(obj => obj.id !== id);

    setSettings({
      ...settings,
      objection_handling: updatedObjections,
    });
  };

  const handleAddObjection = () => {
    if (!settings) return;

    const newObjection: ObjectionItem = {
      id: Date.now().toString(),
      title: 'New Objection',
      response: '',
      category: 'other',
      order: settings.objection_handling.length + 1,
    };

    setSettings({
      ...settings,
      objection_handling: [...settings.objection_handling, newObjection],
    });
  };

  const handleGenerateObjectionResponse = async (objectionId: string, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      if (!settings) throw new Error('No settings loaded');

      const objection = settings.objection_handling.find(obj => obj.id === objectionId);
      if (!objection) throw new Error('Objection not found');

      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'call',
        section: 'objection_handling',
        context: `Objection: "${objection.title}"`,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });

      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating objection response:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  const script1Fields: { key: keyof CallScript1; title: string; placeholder: string }[] = [
    { key: 'greetings', title: 'Greetings', placeholder: 'Hi {First_Name}, this is {Agent_Name} calling from {Company}...' },
    { key: 'introduction', title: 'Introduction', placeholder: 'I\'m reaching out because we help companies like {Company_Name}...' },
    { key: 'why_we_are_calling', title: 'Why We Are Calling (1st Pitch)', placeholder: 'The reason I\'m calling is that many companies in {Industry}...' },
    { key: 'elaboration_credibility', title: 'Elaboration & Credibility (2nd Pitch)', placeholder: 'We work with companies in your industry to help them increase qualified leads...' },
    { key: 'engagement', title: 'Engagement', placeholder: 'I\'m curious - are you finding that your team has enough time to follow up...' },
    { key: 'qualification', title: 'Qualification', placeholder: 'Can I ask - what are you currently using for lead generation...' },
    { key: 'next_steps', title: 'Next Steps', placeholder: 'Based on what you\'ve shared, I think we could really help...' },
    { key: 'booking', title: 'Booking', placeholder: 'I have availability Tuesday at 2pm or Thursday at 10am...' },
    { key: 'closing', title: 'Closing', placeholder: 'Great, I\'ll get that booked in. Really looking forward to showing you...' },
  ];

  const script2Fields: { key: keyof CallScript2; title: string; placeholder: string }[] = [
    { key: 'greetings', title: 'Greetings', placeholder: 'Hi {First_Name}, it\'s {Agent_Name} again from {Company}...' },
    { key: 'introduction', title: 'Introduction', placeholder: 'We spoke about a week ago about improving {Company_Name}\'s...' },
    { key: 'why_we_are_calling_again', title: 'Why We Are Calling Again (Reminding)', placeholder: 'I wanted to follow up because I\'ve been thinking about what you mentioned...' },
    { key: 'engagement', title: 'Engagement', placeholder: 'Have you had a chance to think about our last conversation...' },
    { key: 'qualification_if_not_done', title: 'Qualification (If Not Done)', placeholder: 'What would need to happen for this to become a priority...' },
    { key: 'next_steps', title: 'Next Steps', placeholder: 'I don\'t want to keep calling if the timing isn\'t right...' },
    { key: 'booking', title: 'Booking', placeholder: 'Would Wednesday at 3pm or Friday at 11am work for you...' },
    { key: 'closing', title: 'Closing', placeholder: 'Perfect, I\'ll get that set up. Thanks for your time today...' },
  ];

  return (
    <div className={`max-w-full ${hasChanges || saving || saved ? 'pb-28' : ''}`}>
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <AgentAvatarUpload
            agentType="call"
            avatarUrl={agentAvatarUrl}
            onAvatarChange={setAgentAvatarUrl}
          />
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display mb-2">Call Agent</h2>
            <p className="text-gray-600">
              Configure your AI calling agent's scripts and behavior
            </p>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowTestModal(true)}
            className="px-4 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold"
          >
            <TestTube className="w-5 h-5" />
            Test Agent
          </button>
        )}
      </div>

      {!canEdit && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">View Only</p>
            <p className="text-xs text-blue-800">
              Only admins can edit agent settings. Contact your admin to make changes.
            </p>
          </div>
        </div>
      )}

      <DraftBanner
        draftCount={draft ? 1 : 0}
        agentType="Call"
        loading={draftsLoading}
        onApplyDrafts={applyDrafts}
        onDismissDrafts={() => setDraft(null)}
      />

      <div className={`space-y-6 ${!canEdit ? 'pointer-events-none opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <UserCog className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Agent Identity & Behavior</h3>
              <p className="text-sm text-gray-500">Configure your agent's personality and communication style</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Row 1 Left: Agent (combo with inline name editing) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Agent</label>
              {vapiAgentsLoading ? (
                <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-400 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading agents...
                </div>
              ) : vapiAgents.length === 0 ? (
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-400 bg-gray-50">
                  No agents available
                </div>
              ) : !isEditingAgentName ? (
                <div className="relative">
                  <SimpleCustomSelect
                    value={settings.vapi_agent_id || ''}
                    onChange={(value) => {
                      const agent = vapiAgents.find(a => a.vapi_agent_internal_id === value);
                      if (agent) {
                        setSettings({
                          ...settings,
                          vapi_agent_id: agent.vapi_agent_internal_id,
                          agent_name: agent.agent_name || '',
                          gender: agent.vapi_agent_gender || '',
                        });
                      }
                    }}
                    options={vapiAgents.map(a => ({
                      value: a.vapi_agent_internal_id,
                      label: a.agent_name || `${(a.vapi_agent_gender || 'unknown').charAt(0).toUpperCase()}${(a.vapi_agent_gender || 'unknown').slice(1)} Agent`,
                    }))}
                    placeholder="Select agent"
                  />
                  {settings.vapi_agent_id && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingNameValue(settings.agent_name || '');
                        setIsEditingAgentName(true);
                      }}
                      className="absolute top-1/2 -translate-y-1/2 right-10 p-1 rounded-md text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/10 transition-all"
                      title="Edit agent name"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative flex items-center">
                  <input
                    type="text"
                    autoFocus
                    value={editingNameValue}
                    onChange={(e) => setEditingNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmed = editingNameValue.trim();
                        if (trimmed) {
                          setSettings({ ...settings, agent_name: trimmed });
                          setVapiAgents(prev => prev.map(a =>
                            a.vapi_agent_internal_id === settings.vapi_agent_id
                              ? { ...a, agent_name: trimmed }
                              : a
                          ));
                        }
                        setIsEditingAgentName(false);
                      }
                      if (e.key === 'Escape') {
                        setIsEditingAgentName(false);
                      }
                    }}
                    placeholder="Enter agent name"
                    className="w-full px-4 py-2.5 pr-10 bg-gradient-to-br from-white to-gray-50 border-2 border-[#1875FF] rounded-xl ring-2 ring-[#1875FF]/50 text-sm font-medium transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = editingNameValue.trim();
                      if (trimmed) {
                        setSettings({ ...settings, agent_name: trimmed });
                        setVapiAgents(prev => prev.map(a =>
                          a.vapi_agent_internal_id === settings.vapi_agent_id
                            ? { ...a, agent_name: trimmed }
                            : a
                        ));
                      }
                      setIsEditingAgentName(false);
                    }}
                    className="absolute right-2.5 p-1 rounded-md text-green-500 hover:text-green-600 hover:bg-green-50 transition-all"
                    title="Confirm name"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Row 1 Right: Tone */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                Tone
                <MessagingStyleTooltip agentType="call" />
              </label>
              <SimpleCustomSelect
                value={settings.tone || 'friendly_casual'}
                onChange={(value) => setSettings({ ...settings, tone: value })}
                options={TONE_OPTIONS}
                placeholder="Select style"
              />
            </div>

            {/* Row 2 Left: Gender (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <div className="w-full px-4 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-900 min-h-[42px] flex items-center">
                {settings.gender ? settings.gender.charAt(0).toUpperCase() + settings.gender.slice(1) : '\u00A0'}
              </div>
            </div>

            {/* Row 2 Right: Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <input
                type="text"
                value={settings.role || ''}
                onChange={(e) => setSettings({ ...settings, role: e.target.value })}
                placeholder="Sales Consultant"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
              />
            </div>

            {/* Row 3 Left: Nationality */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nationality
                {(settings.nationality?.length || 0) > 0 && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    {settings.nationality.length}/3
                  </span>
                )}
              </label>
              <div
                className="w-full min-h-[42px] px-3 py-1.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-[#1875FF]/50 focus-within:border-[#1875FF] text-sm transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 flex flex-wrap items-center gap-1.5 cursor-text"
                onClick={() => {
                  const inp = document.getElementById('nationality-typeahead');
                  if (inp) inp.focus();
                }}
              >
                {(settings.nationality || []).map(n => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1875FF]/10 text-[#1875FF] rounded-md text-xs font-semibold whitespace-nowrap flex-shrink-0"
                  >
                    {n}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettings({
                          ...settings,
                          nationality: settings.nationality.filter(v => v !== n),
                        });
                      }}
                      className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {(settings.nationality?.length || 0) < 3 ? (
                  <input
                    id="nationality-typeahead"
                    type="text"
                    value={nationalityInput}
                    onChange={(e) => {
                      setNationalityInput(e.target.value);
                      setShowNationalitySuggestions(e.target.value.length > 0);
                    }}
                    onFocus={() => nationalityInput.length > 0 && setShowNationalitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowNationalitySuggestions(false), 200)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const trimmed = nationalityInput.trim();
                        if (trimmed && !(settings.nationality || []).includes(trimmed)) {
                          setSettings({
                            ...settings,
                            nationality: [...(settings.nationality || []), trimmed],
                          });
                        }
                        setNationalityInput('');
                        setShowNationalitySuggestions(false);
                      }
                      if (e.key === 'Backspace' && !nationalityInput && (settings.nationality || []).length > 0) {
                        setSettings({
                          ...settings,
                          nationality: settings.nationality.slice(0, -1),
                        });
                      }
                    }}
                    placeholder={(settings.nationality || []).length === 0 ? 'Type nationality...' : ''}
                    className="flex-1 min-w-[80px] outline-none bg-transparent text-sm font-medium py-0.5"
                  />
                ) : (
                  <span className="text-xs text-gray-400 py-0.5">Max 3 reached</span>
                )}
              </div>
              {showNationalitySuggestions && (() => {
                const filtered = COMMON_NATIONALITIES.filter(
                  n => n.toLowerCase().includes(nationalityInput.toLowerCase()) &&
                    !(settings.nationality || []).includes(n)
                );
                return filtered.length > 0 ? (
                  <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                    {filtered.map((nat, idx) => (
                      <button
                        key={nat}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!(settings.nationality || []).includes(nat)) {
                            setSettings({
                              ...settings,
                              nationality: [...(settings.nationality || []), nat],
                            });
                          }
                          setNationalityInput('');
                          setShowNationalitySuggestions(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        {nat}
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Row 3 Right: Objective */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Objective</label>
              <input
                type="text"
                value={settings.objective || ''}
                onChange={(e) => setSettings({ ...settings, objective: e.target.value })}
                placeholder="Book a meeting"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
              />
            </div>

            {/* Row 4 Left: Location */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <input
                type="text"
                value={locationInput}
                onChange={(e) => {
                  setLocationInput(e.target.value);
                  setShowLocationSuggestions(e.target.value.length > 0);
                  setSettings({ ...settings, location: e.target.value });
                }}
                onFocus={() => locationInput.length > 0 && setShowLocationSuggestions(true)}
                onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setShowLocationSuggestions(false);
                  }
                }}
                placeholder="City, Country"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
              />
              {showLocationSuggestions && (() => {
                const filtered = COMMON_LOCATIONS.filter(
                  loc => loc.toLowerCase().includes(locationInput.toLowerCase())
                );
                return filtered.length > 0 ? (
                  <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                    {filtered.map((loc, idx) => (
                      <button
                        key={loc}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setLocationInput(loc);
                          setSettings({ ...settings, location: loc });
                          setShowLocationSuggestions(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Row 4 Right: Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <SimpleCustomSelect
                value={settings.phone_number_id || phoneNumbers.find(p => p.number === (settings.phone_number || settings.calling_number))?.value || ''}
                onChange={(value) => {
                  const selected = phoneNumbers.find(p => p.value === value);
                  setSettings({ ...settings, phone_number_id: value, phone_number: selected?.number || settings.phone_number });
                }}
                options={phoneNumbers}
                placeholder={phoneNumbers.length > 0 ? "Select a number" : "No active numbers available"}
              />
            </div>

            {/* Row 5 Left: Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
              <MultiSelectDropdown
                value={settings.languages || ['english']}
                onChange={(values) => setSettings({ ...settings, languages: values })}
                options={[
                  { value: 'english', label: 'English' },
                ]}
                placeholder="Select languages"
              />
            </div>

            {/* Row 5 Right: Max Calls Per Lead */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Calls Per Lead</label>
              <input
                type="number"
                value={settings.max_calls_per_lead}
                onChange={(e) => {
                  const val = Math.min(Math.max(parseInt(e.target.value) || 3, 3), 8);
                  setSettings({ ...settings, max_calls_per_lead: val });
                }}
                min="3"
                max="8"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-6 space-y-6 ${!canEdit ? 'opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
                <ScrollText className="w-5 h-5 text-[#1875FF]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900 font-display">Call Scripts</h3>
                  {Object.keys(templateStatuses).length > 0 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border border-[#1875FF]/15">
                      <span className="text-xs font-bold text-[#1875FF]">
                        {CALL_SCRIPT_SECTIONS.filter(s => templateStatuses[s] === 'approved').length}
                      </span>
                      <span className="text-xs text-gray-400">/</span>
                      <span className="text-xs font-medium text-gray-500">{CALL_SCRIPT_SECTIONS.length}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500">Manage scripts your agent follows during calls</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveScript(1)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeScript === 1
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-lg hover:scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">1</span>
                  First Call
                </span>
              </button>
              <button
                onClick={() => setActiveScript(2)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeScript === 2
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-lg hover:scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">2</span>
                  Follow-up
                </span>
              </button>
              <button
                onClick={() => setActiveScript(3)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeScript === 3
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-lg hover:scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">3</span>
                  Objection Handling
                </span>
              </button>
            </div>
          </div>

          {activeScript === 3 ? (
            <div className="space-y-4">
              <ObjectionHandlingSection
                objections={settings.objection_handling || []}
                onUpdateObjection={handleUpdateObjection}
                onDeleteObjection={handleDeleteObjection}
                onAddObjection={handleAddObjection}
                onGenerateAI={handleGenerateObjectionResponse}
                onCardDirtyChange={handleCardDirtyChange}
                variables={TEMPLATE_VARIABLES}
              />
              {settings.objection_handling?.length > 0 && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2">
                    {templateStatuses['objection_handling'] === 'approved' ? (
                      <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        Approved
                      </span>
                    ) : templateStatuses['objection_handling'] === 'draft' ? (
                      <span className="px-2.5 py-0.5 bg-[#05E0D1]/10 text-[#0bb5a5] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#05E0D1]/25">
                        Draft - Review Pending
                        <Eye className="w-3 h-3" />
                      </span>
                    ) : null}
                    <span className="text-sm text-gray-600">
                      {settings.objection_handling.length} objection{settings.objection_handling.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={handleApproveObjections}
                    disabled={approvingSection === 'objection_handling'}
                    className="px-4 py-2 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm font-medium flex items-center gap-2"
                  >
                    {approvingSection === 'objection_handling' && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save & Approve Objections
                  </button>
                </div>
              )}
            </div>
          ) : activeScript === 1 ? (
            <div className="space-y-4">
              {script1Fields.map((field) => {
                const sectionKey = `script_1_${field.key}` as CallScriptSection;
                return (
                  <ModernScriptCard
                    key={sectionKey}
                    title={field.title}
                    content={settings.script_1[field.key]}
                    placeholder={field.placeholder}
                    onUpdate={(content) => updateScriptContent(1, field.key, content)}
                    onGenerateAI={(tone, length, currentContent) => handleGenerateScript(1, field.key, tone, length, currentContent)}
                    onDirtyChange={(isDirty) => handleCardDirtyChange(sectionKey, isDirty)}
                    templateStatus={templateStatuses[sectionKey]}
                    onApproveTemplate={async (content) => {
                      await handleApproveSection(sectionKey, content);
                    }}
                    approvingTemplate={approvingSection === sectionKey}
                    variables={TEMPLATE_VARIABLES}
                    readOnly={!canEdit}
                  />
                );
              })}
            </div>
          ) : activeScript === 2 ? (
            <div className="space-y-4">
              {script2Fields.map((field) => {
                const sectionKey = `script_2_${field.key}` as CallScriptSection;
                return (
                  <ModernScriptCard
                    key={sectionKey}
                    title={field.title}
                    content={settings.script_2[field.key]}
                    placeholder={field.placeholder}
                    onUpdate={(content) => updateScriptContent(2, field.key, content)}
                    onGenerateAI={(tone, length, currentContent) => handleGenerateScript(2, field.key, tone, length, currentContent)}
                    onDirtyChange={(isDirty) => handleCardDirtyChange(sectionKey, isDirty)}
                    templateStatus={templateStatuses[sectionKey]}
                    onApproveTemplate={async (content) => {
                      await handleApproveSection(sectionKey, content);
                    }}
                    approvingTemplate={approvingSection === sectionKey}
                    variables={TEMPLATE_VARIABLES}
                    readOnly={!canEdit}
                  />
                );
              })}
            </div>
          ) : null}
        </div>

        <div className={`bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5 ${!canEdit ? 'pointer-events-none' : ''}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Agent Rules</h3>
              <p className="text-sm text-gray-500">Define what your agent should and shouldn't do during calls</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 font-display">Do This</h4>
                  {settings.do_rules.length > 0 && (
                    <span className="text-xs text-gray-400 font-medium">{settings.do_rules.length}/7</span>
                  )}
                </div>
                {settings.do_rules.length < 7 && (
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        do_rules: [...settings.do_rules, { id: crypto.randomUUID(), text: '' }],
                      });
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1875FF] hover:text-[#05E0D1] rounded-lg hover:bg-[#1875FF]/5 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Rule
                  </button>
                )}
              </div>

              {settings.do_rules.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <CheckCircle2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No rules added yet</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Add Rule" to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {settings.do_rules.map((rule, index) => (
                    <div key={rule.id} className="flex items-start gap-2.5 group">
                      <span className="mt-2.5 text-xs font-semibold text-emerald-500 w-5 text-right shrink-0">{index + 1}.</span>
                      <textarea
                        value={rule.text}
                        onChange={(e) => {
                          const updated = settings.do_rules.map(r =>
                            r.id === rule.id ? { ...r, text: e.target.value } : r
                          );
                          setSettings({ ...settings, do_rules: updated });
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        placeholder="E.g., Always confirm the prospect's availability before booking"
                        className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none overflow-hidden"
                        rows={1}
                      />
                      <button
                        onClick={() => {
                          setSettings({
                            ...settings,
                            do_rules: settings.do_rules.filter(r => r.id !== rule.id),
                          });
                        }}
                        className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-red-50 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 font-display">Don't Do This</h4>
                  {settings.dont_rules.length > 0 && (
                    <span className="text-xs text-gray-400 font-medium">{settings.dont_rules.length}/7</span>
                  )}
                </div>
                {settings.dont_rules.length < 7 && (
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        dont_rules: [...settings.dont_rules, { id: crypto.randomUUID(), text: '' }],
                      });
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1875FF] hover:text-[#05E0D1] rounded-lg hover:bg-[#1875FF]/5 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Rule
                  </button>
                )}
              </div>

              {settings.dont_rules.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <XCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No rules added yet</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Add Rule" to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {settings.dont_rules.map((rule, index) => (
                    <div key={rule.id} className="flex items-start gap-2.5 group">
                      <span className="mt-2.5 text-xs font-semibold text-red-400 w-5 text-right shrink-0">{index + 1}.</span>
                      <textarea
                        value={rule.text}
                        onChange={(e) => {
                          const updated = settings.dont_rules.map(r =>
                            r.id === rule.id ? { ...r, text: e.target.value } : r
                          );
                          setSettings({ ...settings, dont_rules: updated });
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        placeholder="E.g., Never discuss competitor pricing or make guarantees"
                        className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none overflow-hidden"
                        rows={1}
                      />
                      <button
                        onClick={() => {
                          setSettings({
                            ...settings,
                            dont_rules: settings.dont_rules.filter(r => r.id !== rule.id),
                          });
                        }}
                        className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {canEdit && (
        <FloatingSaveBar
          hasChanges={hasChanges}
          saving={saving}
          saved={saved}
          lastSaved={lastSaved}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}

      <TestAgentModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        agentType="call"
      />
    </div>
  );
}
